// Global sound settings — written by React, read by playClick etc.
export const soundSettings = {
  clicks: true,
  chat: true,
  workflow: true,
};

// Workflow sounds are suppressed when the tab is backgrounded; chat sounds aren't.
const workflowMuted = (): boolean => !soundSettings.workflow || document.hidden;

// Lazily created on first user gesture to avoid Chrome's autoplay warning
let ctx: AudioContext | null = null;

if (typeof document !== "undefined" && typeof AudioContext !== "undefined") {
  const init = () => {
    if (!ctx) {
      ctx = new AudioContext();
      setTimeout(getReverb, 0);
    }
    document.removeEventListener("pointerdown", init, true);
  };
  document.addEventListener("pointerdown", init, true);
}

function getContext(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

function cleanup(...nodes: AudioNode[]): void {
  const last = nodes[nodes.length - 1];
  if (last && "onended" in last) {
    (last as AudioBufferSourceNode | OscillatorNode).onended = () => {
      for (const n of nodes) n.disconnect();
    };
  }
}

// Long diffuse hall — sounds dissolve into it and hang there
let reverbNode: ConvolverNode | null = null;
let reverbGain: GainNode | null = null;

function getReverb(): [ConvolverNode, GainNode] | undefined {
  const c = getContext();
  if (!c) return;
  if (!reverbNode) {
    const length = c.sampleRate * 2.5;
    const impulse = c.createBuffer(2, length, c.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        const t = i / length;
        // Slow exponential decay with stereo decorrelation
        data[i] =
          (Math.random() * 2 - 1) *
          Math.exp(-t * 3) *
          0.25 *
          (1 + Math.sin(i * 0.007 * (ch + 1)) * 0.4);
      }
    }
    reverbNode = c.createConvolver();
    reverbNode.buffer = impulse;
    reverbGain = c.createGain();
    reverbGain.gain.value = 0.35;
    reverbNode.connect(reverbGain);
    reverbGain.connect(c.destination);
  }
  return [reverbNode, reverbGain!];
}

// --- Step sound bank ---

export type StepSoundParams = {
  frequency: number;
  detune: number;
  synth: number;
};

const SYNTH_COUNT = 4;

// B – D♯ – F♯ – A♯ – C♯ – E♯ (whole-tone, dreamy) × 4 synth voices
export const stepSoundBank: StepSoundParams[] = Array.from(
  { length: 6 * SYNTH_COUNT },
  (_, i) => {
    const notes = [246.94, 311.13, 369.99, 466.16, 277.18, 349.23];
    return {
      frequency: notes[i % 6]!,
      detune: 0,
      synth: Math.floor(i / 6),
    };
  }
);

// --- Synth engines ---

// Shared output routing — dry/wet split into reverb
function createOutput(
  c: AudioContext,
  reverb: ConvolverNode,
  dryLevel: number,
  wetLevel: number
): [input: GainNode, cleanup: AudioNode[]] {
  const input = c.createGain();
  input.gain.value = 1;

  const dry = c.createGain();
  dry.gain.value = dryLevel;
  input.connect(dry);
  dry.connect(c.destination);

  const wet = c.createGain();
  wet.gain.value = wetLevel;
  input.connect(wet);
  wet.connect(reverb);

  return [input, [input, dry, wet]];
}

// 0: Drift — detuned sines beating against each other, sub-octave, warm pad
function synthDrift(c: AudioContext, t: number, f: number, out: GainNode) {
  const dur = 1.592;
  const detunes = [-6.8, 0, 6.8];
  const oscs: OscillatorNode[] = [];

  for (const det of detunes) {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    osc.detune.value = det + (Math.random() * 4 - 2);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.setTargetAtTime(0.055, t, 0.068);
    gain.gain.setTargetAtTime(0.001, t + 0.3, 0.6);

    osc.connect(gain);
    gain.connect(out);
    oscs.push(osc);
    cleanup(osc, gain);
  }

  // Sub-octave warmth
  const sub = c.createOscillator();
  sub.type = "sine";
  sub.frequency.value = f / 2;
  const subGain = c.createGain();
  subGain.gain.setValueAtTime(0.001, t);
  subGain.gain.setTargetAtTime(0.035, t, 0.1);
  subGain.gain.setTargetAtTime(0.001, t + 0.25, 0.6);
  sub.connect(subGain);
  subGain.connect(out);

  for (const osc of oscs) {
    osc.start(t);
    osc.stop(t + dur);
  }
  sub.start(t);
  sub.stop(t + dur);
  cleanup(sub, subGain);
}

// 1: Glass — bowed harmonics, slow tremolo, bright but filtered
function synthGlass(c: AudioContext, t: number, f: number, out: GainNode) {
  const dur = 1.2;

  // Fundamental + octave — like harmonics on a glass
  const partials = [1, 2];
  const volumes = [0.05, 0.04];

  for (let p = 0; p < partials.length; p++) {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f * partials[p]!;
    // Detune for life
    osc.detune.value = Math.random() * 7 - 3.5;

    // Slow tremolo via LFO — gives it a bowed, living quality
    const lfo = c.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 3 + Math.random() * 2;
    const lfoGain = c.createGain();
    lfoGain.gain.value = volumes[p]! * 0.3;
    lfo.connect(lfoGain);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.setTargetAtTime(volumes[p]!, t, 0.01);
    gain.gain.setTargetAtTime(0.001, t + 0.4, 0.3);
    lfoGain.connect(gain.gain);

    // Highpass to keep only the shimmer
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = f * 0.8;
    hp.Q.value = 0.3;

    osc.connect(hp);
    hp.connect(gain);
    gain.connect(out);

    osc.start(t);
    osc.stop(t + dur);
    lfo.start(t);
    lfo.stop(t + dur);
    cleanup(osc, hp, gain);
    cleanup(lfo, lfoGain);
  }
}

// 2: Breath — mostly filtered noise tuned to the note, ghostly
function synthBreath(c: AudioContext, t: number, f: number, out: GainNode) {
  const dur = 1.283;

  // Pitched noise — narrow bandpass on white noise
  const noise = c.createBufferSource();
  const len = c.sampleRate * dur;
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.5;
  }
  noise.buffer = buf;

  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(f * 1.5, t);
  bp.frequency.setTargetAtTime(f * 0.8, t + 0.3, 0.3);
  bp.Q.value = 6;

  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0.001, t);
  noiseGain.gain.setTargetAtTime(0.04, t, 0.011);
  noiseGain.gain.setTargetAtTime(0.001, t + 0.25, 1);

  noise.connect(bp);
  bp.connect(noiseGain);
  noiseGain.connect(out);

  // Ghost tone underneath — barely audible sine anchors the pitch
  const ghost = c.createOscillator();
  ghost.type = "sine";
  ghost.frequency.value = f;
  ghost.detune.value = Math.random() * 6 - 3;

  const ghostGain = c.createGain();
  ghostGain.gain.setValueAtTime(0.001, t);
  ghostGain.gain.setTargetAtTime(0.02, t, 0.011);
  ghostGain.gain.setTargetAtTime(0.001, t + 0.2, 1);

  ghost.connect(ghostGain);
  ghostGain.connect(out);

  noise.start(t);
  ghost.start(t);
  ghost.stop(t + dur);
  cleanup(noise, bp, noiseGain);
  cleanup(ghost, ghostGain);
}

// 3: Bell — FM synthesis with harmonic ratio, stays in key
function synthBell(c: AudioContext, t: number, f: number, out: GainNode) {
  const dur = 0.806;

  // FM with 2:1 ratio — sidebands at octaves, always consonant
  const mod = c.createOscillator();
  mod.type = "sine";
  mod.frequency.value = f * 2;

  // Modulation depth decays — complex attack settles to pure tone
  const modGain = c.createGain();
  modGain.gain.setValueAtTime(f * 0.6, t);
  modGain.gain.setTargetAtTime(f * 0.05, t, 0.25);

  const carrier = c.createOscillator();
  carrier.type = "sine";
  carrier.frequency.value = f;

  mod.connect(modGain);
  modGain.connect(carrier.frequency);

  const env = c.createGain();
  env.gain.setValueAtTime(0.001, t);
  env.gain.setTargetAtTime(0.06, t, 0.001);
  env.gain.setTargetAtTime(0.001, t + 0.3, 1.5);

  // Lowpass to soften the FM overtones
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(f * 4, t);
  lp.frequency.setTargetAtTime(f * 1.2, t + 0.2, 0.4);
  lp.Q.value = 0.5;

  carrier.connect(lp);
  lp.connect(env);
  env.connect(out);

  mod.start(t);
  carrier.start(t);
  mod.stop(t + dur);
  carrier.stop(t + dur);
  cleanup(mod, modGain);
  cleanup(carrier, lp, env);
}

const synthEngines = [synthDrift, synthGlass, synthBreath, synthBell];

const synthConfigs = [
  {
    dry: 0.4,
    wet: 0.7,
    brightness: 3,
    resonance: 0.5,
    eqLow: 0,
    eqMid: 0,
    eqHigh: 0,
  },
  {
    dry: 0.4,
    wet: 0.8,
    brightness: 2,
    resonance: 0.5,
    eqLow: 3,
    eqMid: 0,
    eqHigh: 0,
  },
  {
    dry: 0.4,
    wet: 0.9,
    brightness: 3,
    resonance: 0.1,
    eqLow: 2.45,
    eqMid: 0,
    eqHigh: -12,
  },
  {
    dry: 0.4,
    wet: 0.8,
    brightness: 4.5,
    resonance: 0.5,
    eqLow: 0,
    eqMid: 0,
    eqHigh: 0,
  },
];

// --- Sound functions ---

export function playStepSound({ frequency, synth }: StepSoundParams): void {
  if (workflowMuted()) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;
  const [reverb] = getReverb()!;

  const config = synthConfigs[synth % SYNTH_COUNT]!;

  // Lowpass filter — brightness controls how open it is
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(frequency * config.brightness, t);
  filter.frequency.setTargetAtTime(frequency * 0.8, t + 0.2, 0.3);
  filter.Q.value = config.resonance;

  // EQ chain — only create bands with non-zero gain
  let lastNode: AudioNode = filter;
  const eqNodes: AudioNode[] = [];

  if (config.eqLow !== 0) {
    const low = c.createBiquadFilter();
    low.type = "lowshelf";
    low.frequency.value = 300;
    low.gain.value = config.eqLow;
    lastNode.connect(low);
    lastNode = low;
    eqNodes.push(low);
  }

  if (config.eqMid !== 0) {
    const mid = c.createBiquadFilter();
    mid.type = "peaking";
    mid.frequency.value = 1000;
    mid.Q.value = 1;
    mid.gain.value = config.eqMid;
    lastNode.connect(mid);
    lastNode = mid;
    eqNodes.push(mid);
  }

  if (config.eqHigh !== 0) {
    const high = c.createBiquadFilter();
    high.type = "highshelf";
    high.frequency.value = 3000;
    high.gain.value = config.eqHigh;
    lastNode.connect(high);
    lastNode = high;
    eqNodes.push(high);
  }

  const [output, outputNodes] = createOutput(c, reverb, config.dry, config.wet);
  lastNode.connect(output);

  // Dispatch to the synth engine
  synthEngines[synth % SYNTH_COUNT]!(c, t, frequency, filter);

  // Cleanup the shared output chain via a silent oscillator that ends last
  const sentinel = c.createOscillator();
  sentinel.frequency.value = 0;
  const silentGain = c.createGain();
  silentGain.gain.value = 0;
  sentinel.connect(silentGain);
  silentGain.connect(c.destination);
  sentinel.start(t);
  sentinel.stop(t + 2);
  cleanup(sentinel, silentGain, filter, ...eqNodes, ...outputNodes);
}

// Granular crackle — two micro noise grains with slight spacing
export function playClick(): void {
  if (!soundSettings.clicks) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;

  for (let g = 0; g < 2; g++) {
    const noise = c.createBufferSource();
    const len = c.sampleRate * (0.004 + Math.random() * 0.003);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / 30);
    }
    noise.buffer = buf;

    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 2800 + Math.random() * 800;
    bp.Q.value = 2 + Math.random() * 2;

    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 3000;
    lp.Q.value = 0.5;

    const gain = c.createGain();
    gain.gain.setValueAtTime(g === 0 ? 0.2 : 0.12, t + g * 0.006);

    noise.connect(bp);
    bp.connect(lp);
    lp.connect(gain);
    gain.connect(c.destination);
    noise.start(t + g * 0.006);
    cleanup(noise, bp, lp, gain);
  }
}

// Softer single grain — the button coming back up on release
export function playRelease(): void {
  if (!soundSettings.clicks) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;

  const noise = c.createBufferSource();
  const len = c.sampleRate * (0.003 + Math.random() * 0.002);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / 24);
  }
  noise.buffer = buf;

  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1900 + Math.random() * 500;
  bp.Q.value = 2 + Math.random() * 2;

  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2200;
  lp.Q.value = 0.5;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.09, t);

  noise.connect(bp);
  bp.connect(lp);
  lp.connect(gain);
  gain.connect(c.destination);
  noise.start(t);
  cleanup(noise, bp, lp, gain);
}

// Soft single ping — F♯5, gentle decay
export function playChatDone(): void {
  if (!soundSettings.chat) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;

  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.value = 739.99; // F♯5

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.08, t);
  gain.gain.setTargetAtTime(0.001, t + 0.03, 0.1);

  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1800;
  filter.Q.value = 0.5;

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);

  osc.start(t);
  osc.stop(t + 0.3);
  cleanup(osc, filter, gain);
}

function synthChatNeedsInput(out?: AudioNode): void {
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;
  const dest = out ?? c.destination;

  // [freq, delay, duration]
  const taps: [number, number, number][] = [
    [493.88, 0, 0.14], // B4
    [932.33, 0.1, 0.18], // A♯5
  ];

  for (const [freq, delay, dur] of taps) {
    // Tonal body
    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq * 1.02, t + delay);
    osc.frequency.exponentialRampToValueAtTime(freq, t + delay + 0.03);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.08, t + delay);
    gain.gain.setTargetAtTime(0.001, t + delay + 0.02, dur * 0.35);

    // Woody knock transient
    const knock = c.createBufferSource();
    const knockBuf = c.createBuffer(1, c.sampleRate * 0.008, c.sampleRate);
    const knockData = knockBuf.getChannelData(0);
    for (let i = 0; i < knockData.length; i++) {
      knockData[i] = (Math.random() * 2 - 1) * Math.exp(-i / 15);
    }
    knock.buffer = knockBuf;

    const knockFilter = c.createBiquadFilter();
    knockFilter.type = "bandpass";
    knockFilter.frequency.value = freq * 2;
    knockFilter.Q.value = 3;

    const knockGain = c.createGain();
    knockGain.gain.setValueAtTime(0.08, t + delay);

    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = freq * 3;
    filter.Q.value = 0.7;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest);

    knock.connect(knockFilter);
    knockFilter.connect(knockGain);
    knockGain.connect(dest);

    osc.start(t + delay);
    osc.stop(t + delay + dur);
    knock.start(t + delay);
    cleanup(osc, filter, gain);
    cleanup(knock, knockFilter, knockGain);
  }
}

// Ascending double-tap — B4 → A♯5
export function playChatNeedsInput(): void {
  if (!soundSettings.chat) return;
  synthChatNeedsInput();
}

// The needs-you tap heard through a wall. "Muffled" is a low-pass, not a
// high-pass: dropping the bright high transient makes a background item in the
// attention menu draw the eye without being mistaken for the workflow you're on.
export function playChatNeedsInputMuffled(): void {
  if (!soundSettings.chat) return;
  const c = getContext();
  if (!c) return;

  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 350;
  lp.Q.value = 0.7;

  const gain = c.createGain();
  gain.gain.value = 0.8;

  lp.connect(gain);
  gain.connect(c.destination);
  synthChatNeedsInput(lp);
}

function synthPop(): void {
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;

  const baseFreq = 454 + Math.random() * 80; // center ≈ B4

  // Tonal body — sine ping
  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(baseFreq, t);

  const toneGain = c.createGain();
  toneGain.gain.setValueAtTime(0.07 + Math.random() * 0.02, t);
  toneGain.gain.setTargetAtTime(0.001, t + 0.02, 0.04);

  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 300;
  lp.Q.value = 0.7;

  osc.connect(lp);
  lp.connect(toneGain);
  toneGain.connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.15);

  // Noise transient — tiny filtered burst that gives the "pop" its click
  const noise = c.createBufferSource();
  const len = c.sampleRate * (0.005 + Math.random() * 0.003);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / 20);
  }
  noise.buffer = buf;

  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = baseFreq * 3 + Math.random() * 400;
  bp.Q.value = 1.5 + Math.random();

  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0.105, t);

  noise.connect(bp);
  bp.connect(noiseGain);
  noiseGain.connect(c.destination);
  noise.start(t);

  cleanup(osc, lp, toneGain);
  cleanup(noise, bp, noiseGain);
}

// Soft pop — pitched sine with a tiny noise transient for percussive attack.
// Randomized per-play so consecutive pops feel organic (like playClick).
export function playPop(): void {
  if (workflowMuted()) return;
  synthPop();
}

// Ascending major triad chime — congratulatory, quick, subtle
export function playPushLive(): void {
  if (workflowMuted()) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;
  const [reverb] = getReverb()!;
  const [output, outputNodes] = createOutput(c, reverb, 0.5, 0.6);

  // C5 → E5 → G5 → C6 — bright major arpeggio, staggered
  const notes: [number, number][] = [
    [523.25, 0], // C5
    [659.25, 0.09], // E5
    [783.99, 0.18], // G5
    [1046.5, 0.27], // C6
  ];

  for (const [freq, delay] of notes) {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    osc.detune.value = Math.random() * 4 - 2;

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.001, t + delay);
    gain.gain.setTargetAtTime(0.07, t + delay, 0.006);
    gain.gain.setTargetAtTime(0.001, t + delay + 0.06, 0.12);

    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = freq * 3;
    lp.Q.value = 0.5;

    osc.connect(lp);
    lp.connect(gain);
    gain.connect(output);

    osc.start(t + delay);
    osc.stop(t + delay + 0.4);
    cleanup(osc, lp, gain);
  }

  // Cleanup output chain via sentinel
  const sentinel = c.createOscillator();
  sentinel.frequency.value = 0;
  const silentGain = c.createGain();
  silentGain.gain.value = 0;
  sentinel.connect(silentGain);
  silentGain.connect(c.destination);
  sentinel.start(t);
  sentinel.stop(t + 1);
  cleanup(sentinel, silentGain, ...outputNodes);
}

// Twinkling ascending run — bell FM tones with reverb, for the easter-egg spin
export function playSparkle(): void {
  if (!soundSettings.clicks) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;
  const [reverb] = getReverb()!;
  const [output, outputNodes] = createOutput(c, reverb, 0.35, 0.8);

  // Pentatonic cascade — C6, E6, G6, A6, C7, E7
  const notes: [number, number][] = [
    [1046.5, 0],
    [1318.5, 0.05],
    [1568.0, 0.1],
    [1760.0, 0.15],
    [2093.0, 0.2],
    [2637.0, 0.26],
  ];

  for (const [freq, delay] of notes) {
    const mod = c.createOscillator();
    mod.type = "sine";
    mod.frequency.value = freq * 2;

    const modGain = c.createGain();
    modGain.gain.setValueAtTime(freq * 0.4, t + delay);
    modGain.gain.setTargetAtTime(freq * 0.03, t + delay, 0.12);

    const carrier = c.createOscillator();
    carrier.type = "sine";
    carrier.frequency.value = freq;
    carrier.detune.value = Math.random() * 6 - 3;

    mod.connect(modGain);
    modGain.connect(carrier.frequency);

    const gain = c.createGain();
    gain.gain.setValueAtTime(0.001, t + delay);
    gain.gain.setTargetAtTime(0.045, t + delay, 0.004);
    gain.gain.setTargetAtTime(0.001, t + delay + 0.04, 0.18);

    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = freq * 3;
    lp.Q.value = 0.4;

    carrier.connect(lp);
    lp.connect(gain);
    gain.connect(output);

    mod.start(t + delay);
    carrier.start(t + delay);
    mod.stop(t + delay + 0.5);
    carrier.stop(t + delay + 0.5);
    cleanup(mod, modGain);
    cleanup(carrier, lp, gain);
  }

  const sentinel = c.createOscillator();
  sentinel.frequency.value = 0;
  const silentGain = c.createGain();
  silentGain.gain.value = 0;
  sentinel.connect(silentGain);
  silentGain.connect(c.destination);
  sentinel.start(t);
  sentinel.stop(t + 1.5);
  cleanup(sentinel, silentGain, ...outputNodes);
}

// Granular scatter — filtered noise grains tumbling downward
export function playShuffle(): void {
  if (workflowMuted()) return;
  const c = getContext();
  if (!c) return;
  const t = c.currentTime;

  const grainCount = 6;
  for (let g = 0; g < grainCount; g++) {
    const offset = g * 0.032 + Math.random() * 0.008;
    const noise = c.createBufferSource();
    const len = c.sampleRate * (0.01 + Math.random() * 0.008);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / 40);
    }
    noise.buffer = buf;

    const filter = c.createBiquadFilter();
    filter.type = "bandpass";
    // Pitch descends across the grain scatter
    filter.frequency.value =
      2800 - (g / (grainCount - 1)) * 589 + Math.random() * 200;
    filter.Q.value = 5.75;

    const gain = c.createGain();
    const vol = 0.1 * (1 - (g / grainCount) * 0.6);
    gain.gain.setValueAtTime(vol, t + offset);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(c.destination);
    noise.start(t + offset);
    cleanup(noise, filter, gain);
  }
}
